import { Path, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE, REGEX } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import React, { useEffect } from "react";
import LabelHeader from "components/Label/LabelHeader";
import ImageComponent from "components/image/ImageComponent";
import { MstItemDetailWheel } from "../types";

const styles = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box",
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  closeBtn: {},
};

interface IWheelFormProps {
  type: "update" | "create";
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: MstItemDetailWheel) => void;
  isLoading: boolean;
  wheelDetail: MstItemDetailWheel;
}

const wheelFormFieldsReadOnly = [
  { label: "YH商品コード", disabled: true, fieldName: "item_cd" },
  { label: "JANコード", disabled: true, fieldName: "jan_cd" },
  { label: "ブランドコード", disabled: true, fieldName: "brand_cd" },
  { label: "商品名", disabled: true, fieldName: "item_name" },
  { label: "メーカー品番", disabled: true, fieldName: "maker_item_cd" },
  { label: "サイズ", disabled: true, fieldName: "item_size" },
  { label: "商品カテゴリ（大）", disabled: true, fieldName: "item_category_l" },
  { label: "商品カテゴリ（中）", disabled: true, fieldName: "item_category_m" },
  { label: "商品カテゴリ（小）", disabled: true, fieldName: "item_category_s" },
  { label: "定価", disabled: true, fieldName: "list_price" },
  { label: "A表価格", disabled: true, fieldName: "a_price" },
  { label: "", disabled: true, fieldName: "" },
  { label: "メイン仕入先コード", disabled: true, fieldName: "main_vendor_cd" },
  { label: "メイン本部仕入原価", disabled: true, fieldName: "main_purchase_cost" },
  { label: "発注先コード", disabled: true, fieldName: "shipping_dest_cd" },
  { label: "廃番", disabled: true, fieldName: "waste_num_flag" },
  { label: "", disabled: true, fieldName: "none_block_0" },
  {
    label: "ECフラグ",
    disabled: true,
    fieldName: "ec_flag",
    type: "select",
    options: [
      { label: "有効", value: 1 },
      { label: "無効", value: 0 },
    ],
  },
  { label: "二次仕入先コード", disabled: true, fieldName: "vendor_cd_2nd" },
  { label: "三次仕入先コード", disabled: true, fieldName: "vendor_cd_3rd" },
  { label: "発注単位", disabled: true, fieldName: "hatchu_unit" },
  { label: "", disabled: true, fieldName: "none_block_1" },
];

const wheelFormFieldsEdit = [
  {
    label: "リム幅",
    fieldName: "wheel_width",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(4, 1),
        message: MESSAGE.NUMERIC_FORMAT(4, 1),
      },
    },
    type: "number-float",
  },
  {
    label: "ホール数",
    fieldName: "wheel_hole",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(9, 0),
        message: MESSAGE.NUMERIC_FORMAT(9, 0),
      },
    },
    type: "number-float",
  },
  {
    label: "リム径",
    fieldName: "wheel_rim_diam",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(3, 1),
        message: MESSAGE.NUMERIC_FORMAT(3, 1),
      },
    },
    type: "number-float",
  },
  {
    label: "PCD",
    fieldName: "wheel_pcd",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(5, 2),
        message: MESSAGE.NUMERIC_FORMAT(5, 2),
      },
    },
    type: "number-float",
  },
  {
    label: "インセット",
    fieldName: "wheel_inset",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(3, 1),
        message: MESSAGE.NUMERIC_FORMAT(3, 1),
      },
      type: "number-float",
    },
  },
  {
    label: "ハブ径",
    fieldName: "wheel_hub_diam",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(3, 1),
        message: MESSAGE.NUMERIC_FORMAT(3, 1),
      },
      type: "number-float",
    },
  },
  {
    label: "カラー",
    fieldName: "wheel_color_name",
    rules: {
      maxLength: {
        message: MESSAGE.MAX_LENGTH(100),
        value: 100,
      },
    },
  },
  {
    label: "廉価ホイール",
    fieldName: "low_price_flag",
    type: "checkbox",
    checkboxLabel: true,
  },
  {
    label: "輸入車フラグ",
    fieldName: "import_car_flag",
    type: "checkbox",
    checkboxLabel: true,
  },
  {
    label: "オススメ",
    fieldName: "recommend_flag",
    type: "select",
    options: [
      { label: "はい", value: 1 },
      { label: "いいえ", value: 0 },
    ],
  },
  {
    label: "有効/無効",
    fieldName: "status_flag",
    type: "select",
    options: [
      { label: "有効", value: 1 },
      { label: "無効", value: 0 },
    ],
  },
];

/**
 * WheelFormModal Component
 *
 * Modal form for creating or updating wheel details.
 * Uses react-hook-form for form state management and validation.
 * Displays read-only and editable fields, image preview, and action buttons.
 * @param {object} props - Component props.
 * @param {"update" | "create"} [props.type] - Modal mode, either update or create.
 * @param {boolean} props.visible - Whether the modal is visible.
 * @param {() => void} props.onClose - Callback to close the modal.
 * @param {(form: MstItemDetailWheel) => void} props.onHandleSubmit - Callback for form submission.
 * @param {boolean} props.isLoading - Loading state for the form.
 * @param {MstItemDetailWheel} props.wheelDetail - Wheel detail data for the form.
 */
export default function WheelFormModal({
  type = "update",
  visible,
  onClose,
  onHandleSubmit,
  isLoading,
  wheelDetail,
}: IWheelFormProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MstItemDetailWheel>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      ...wheelDetail,
      ec_flag: !wheelDetail.low_price_flag ? 0 : 1,
      low_price_flag: Boolean(wheelDetail.low_price_flag) as never,
      remarks: wheelDetail.remarks || "",
      import_car_flag: Boolean(wheelDetail.import_car_flag) as never,
    },
  });

  const isUpdate = type === "update";

  const onSubmit = (data: MstItemDetailWheel) => {
    if (isLoading || Object.keys(errors).length > 0) return;
    const requestData: MstItemDetailWheel = {
      ...data,
    };
    onHandleSubmit(requestData);
  };

  useEffect(() => {
    if (wheelDetail && wheelDetail.id) {
      reset({
        ...wheelDetail,
        ec_flag: !wheelDetail.low_price_flag ? 0 : 1,
        low_price_flag: Boolean(wheelDetail.low_price_flag) as never,
        remarks: wheelDetail.remarks || "",
        import_car_flag: Boolean(wheelDetail.import_car_flag) as never,
      });
    }
  }, [wheelDetail?.id]);

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={visible}
      onClose={onClose}
    >
      <Box
        sx={{
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          backgroundColor: "var(--white)",
          padding: { xs: 3, md: 4, xl: "30px 60px" },
          maxWidth: { xs: "90%", lg: "1300px" },
          borderRadius: "21px",
          display: "flex",
          flexDirection: "column",
          rowGap: "20px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "fontWeightMedium",
              color: "var(--gray-700)",
              fontSize: { xs: 28, md: 33 },
            }}
          >
            商品詳細
            <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
            {isUpdate && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 16, md: 21 },
                  fontWeight: "fontWeightMedium",
                  color: "var(--gray-700)",
                  ml: { xs: 0, md: 3 },
                }}
              >
                最終更新日時 {formatDateTime(wheelDetail?.updated_at || "")}
              </Typography>
            )}
          </Typography>
          <CancelButton onClose={onClose} />
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ width: { xs: "100%", md: "calc(60% - 24px)" } }}>
              {wheelFormFieldsReadOnly.map((el) => (
                <FormRow
                  key={el.fieldName}
                  sxLabel={{ pl: 4, width: { xs: "40%", md: "45%" } }}
                  label={el.label}
                  sxBoxField={{ width: { xs: "60%", md: "55%" }, flexGrow: 1 }}
                  sxField={{ ...styles.field }}
                  name={el.fieldName as Path<MstItemDetailWheel>}
                  type={el.type as never}
                  control={control}
                  options={el.type === "select" ? el.options : undefined}
                  disabled={el.disabled}
                />
              ))}
            </Box>
            <Box sx={{ width: { xs: "100%", md: "40%" } }}>
              <LabelHeader
                label="サイズ設定"
                sxBox={{
                  mb: "2px",
                  pl: { xs: "16px", lg: "32px" },
                  height: "48px",
                  backgroundColor: "var(--silver-150)",
                  boxSizing: "border-box",
                }}
              />
              {wheelFormFieldsEdit.map((el) => (
                <FormRow
                  key={el.fieldName}
                  sxLabel={{ pl: 4, width: "40%" }}
                  label={el.label}
                  sxBoxField={{ width: "60%", flexGrow: 1 }}
                  sxField={{ ...styles.field }}
                  name={el.fieldName as Path<MstItemDetailWheel>}
                  type={el.type as never}
                  control={control}
                  rules={el.rules ? el.rules : undefined}
                  options={el.type === "select" ? el.options : undefined}
                  checkboxLabel={el.checkboxLabel}
                />
              ))}

              <ImageComponent
                sx={{
                  mt: 1,
                  mb: { xs: 1, md: 0 },
                  p: 3,
                  border: "2px solid var(--gray-250)",
                  textAlign: "center",
                  margin: "0 auto",
                }}
                objectFit="contain"
                src={wheelDetail.wheel_image_url ?? "/img/wheel/wheel.svg"}
                alt="wheel image"
              />
            </Box>
          </Box>

          <FormRow
            key="remarks"
            sxLabel={{ pl: 4, width: { xs: "40%", md: "calc(((60% - 24px) * 45)/100)" } }}
            label="コメント"
            sxBoxField={{ width: { xs: "60%", md: "55%" }, flexGrow: 1 }}
            sxField={styles.field}
            sxBoxRow={{ mt: 3 }}
            name="remarks"
            control={control}
            rules={{ maxLength: { value: 256, message: MESSAGE.MAX_LENGTH(256) } }}
          />

          <ButtonComponent
            disabled={isLoading || Object.keys(errors).length > 0}
            type="submit"
            label="更新"
            theme="dark"
            sx={{ height: "50px", minWidth: "185px", width: "28%", maxWidth: 239, mt: 2.5 }}
          />
          <Typography
            sx={{ fontSize: { xs: 16, md: 21 }, mt: 2.5 }}
            fontWeight="medium"
            color="var(--gray-700)"
          >
            更新日 {formatDateTime(wheelDetail.updated_at)}
          </Typography>
        </form>
        <CancelButton sx={{ mt: 1.5 }} onClose={onClose} />
      </Box>
    </Modal>
  );
}
